/**
 * static/js/utils/action-menu.js
 *
 * Row action menu (kebab → text dropdown) for public list rows. The canonical
 * pattern across profile pages: a vertical-dots trigger opening a text-only
 * dropdown, matching Payout Methods. Styling lives in the .action-menu* classes
 * (static/css/index.css); this file only drives open/close.
 *
 * Behaviour is delegated off document, so menus rendered after load (rows
 * appended by renderTableRow on topics/services/social/languages) work with no
 * rewiring. Clicking a trigger toggles its sibling panel and closes any others;
 * outside-click and Escape close all.
 *
 * Markup contract (use JN.actionMenu.html() to build it in JS):
 *   <div class="action-menu">
 *     <button type="button" class="action-menu-trigger" aria-haspopup="true" aria-expanded="false">…dots…</button>
 *     <div class="action-menu-panel tw-hidden" role="menu"> …items… </div>
 *   </div>
 *
 * Usage from a JS-rendered row:
 *   JN.actionMenu.html([
 *     { label: 'Edit', onclick: "editFoo(1)" },
 *     { label: 'Delete', onclick: "deleteFoo(1)", danger: true },
 *   ])
 */
(function (window, document) {
  'use strict';

  function panelOf(trigger) {
    var menu = trigger.closest('.action-menu');
    return menu ? menu.querySelector('.action-menu-panel') : null;
  }

  function closeAll(except) {
    document.querySelectorAll('.action-menu-panel:not(.tw-hidden)').forEach(function (panel) {
      if (panel === except) return;
      panel.classList.add('tw-hidden');
      var menu = panel.closest('.action-menu');
      var trigger = menu && menu.querySelector('.action-menu-trigger');
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
    });
  }

  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('.action-menu-trigger');
    if (trigger) {
      e.preventDefault();
      var panel = panelOf(trigger);
      if (!panel) return;
      var willOpen = panel.classList.contains('tw-hidden');
      closeAll(willOpen ? panel : null);
      panel.classList.toggle('tw-hidden', !willOpen);
      trigger.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
      return;
    }
    // A click inside an open panel (e.g. a menu link) should let the action run
    // and then close; a click anywhere else closes all open menus.
    if (!e.target.closest('.action-menu-panel')) closeAll(null);
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') closeAll(null);
  });

  function escapeAttr(value) {
    return String(value).replace(/&/g, '&amp;').replace(/"/g, '&quot;');
  }

  // Build the menu markup string for JS-rendered rows. Each item:
  //   { label, href?, onclick?, danger? }
  // href → an <a>; otherwise a <button>. label is treated as trusted markup
  // (callers pass plain text or already-escaped values, as elsewhere in the
  // renderTableRow helpers).
  function html(items) {
    var body = items.map(function (item) {
      var cls = 'action-menu-item' + (item.danger ? ' danger' : '');
      if (item.href) {
        return '<a href="' + escapeAttr(item.href) + '" class="' + cls + '" role="menuitem">' + item.label + '</a>';
      }
      var onclick = item.onclick ? ' onclick="' + escapeAttr(item.onclick) + '"' : '';
      return '<button type="button" class="' + cls + '" role="menuitem"' + onclick + '>' + item.label + '</button>';
    }).join('');
    return (
      '<div class="action-menu">' +
        '<button type="button" class="action-menu-trigger" aria-label="Actions" aria-haspopup="true" aria-expanded="false">' +
          '<iconify-icon icon="mdi:dots-vertical" class="tw-text-xl"></iconify-icon>' +
        '</button>' +
        '<div class="action-menu-panel tw-hidden" role="menu">' + body + '</div>' +
      '</div>'
    );
  }

  window.JN = window.JN || {};
  window.JN.actionMenu = { html: html, closeAll: closeAll };
})(window, document);
